import {
	type KeyboardEvent,
	useCallback,
	useEffect,
	useId,
	useMemo,
	useRef,
	useState,
} from 'react'

import useAiSearchStore from '@/stores/aiSearchStore'
import useSearchSelectorStore from '@/stores/searchSelectorStore'

import { SEARCH_TYPE_VALUES } from './SearchSelector.constants'
import type {
	SearchSelectorHookResult,
	SearchType,
} from './SearchSelector.types'

const FIRST_OPTION_INDEX = 0
const LAST_OPTION_INDEX = SEARCH_TYPE_VALUES.length - 1

function keepWithinRange(index: number): number {
	return Math.max(FIRST_OPTION_INDEX, Math.min(LAST_OPTION_INDEX, index))
}

const PREVENT_DEFAULT_KEYS = new Set<string>([
	'ArrowDown',
	'ArrowUp',
	'Home',
	'End',
	'Enter',
	' ',
])

export function useSearchSelector(
	options: {
		onAfterSelect?: () => void
		onSearchTypeWillChange?: () => void
	} = {}
): SearchSelectorHookResult {
	const { onAfterSelect, onSearchTypeWillChange } = options
	const rootRef = useRef<HTMLDivElement>(null)
	const buttonRef = useRef<HTMLButtonElement>(null)
	const clearAiSearch = useAiSearchStore((state) => state.clearAiSearch)
	const searchType = useSearchSelectorStore((state) => state.searchType)
	const setSearchType = useSearchSelectorStore((state) => state.setSearchType)

	const instanceId = useId()
	const ids = useMemo(
		() => ({
			comboboxId: `${instanceId}-combobox`,
			headingId: `${instanceId}-heading`,
			listboxId: `${instanceId}-listbox`,
			baseOptionId: `${instanceId}-option`,
		}),
		[instanceId]
	)

	const selected = searchType
	const [isOpen, setIsOpen] = useState(false)
	const [activeIndex, setActiveIndex] = useState(() => {
		const idx = SEARCH_TYPE_VALUES.indexOf(
			useSearchSelectorStore.getState().searchType
		)
		return idx >= 0 ? idx : 0
	})

	const close = useCallback((): void => {
		setIsOpen(false)
		buttonRef.current?.focus()
	}, [])

	const selectType = useCallback(
		(type: SearchType): void => {
			if (type !== searchType) {
				onSearchTypeWillChange?.()
				clearAiSearch()
			}
			setSearchType(type)
			setIsOpen(false)
			onAfterSelect ? onAfterSelect() : buttonRef.current?.focus()
		},
		[
			onAfterSelect,
			onSearchTypeWillChange,
			setSearchType,
			buttonRef,
			clearAiSearch,
			searchType,
		]
	)

	const toggleOpen = useCallback((): void => {
		setIsOpen((open) => !open)
	}, [])

	useEffect(() => {
		if (!isOpen) {
			return
		}
		const handlePointerDown = (event: MouseEvent): void => {
			if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
				setIsOpen(false)
			}
		}
		document.addEventListener('mousedown', handlePointerDown)
		return (): void => {
			document.removeEventListener('mousedown', handlePointerDown)
		}
	}, [isOpen])

	useEffect(() => {
		if (isOpen) {
			setActiveIndex(SEARCH_TYPE_VALUES.indexOf(selected))
		}
	}, [isOpen, selected])

	useEffect(() => {
		if (!isOpen) {
			return
		}
		document
			.getElementById(`${ids.baseOptionId}-${activeIndex}`)
			?.scrollIntoView({ block: 'nearest' })
	}, [activeIndex, ids.baseOptionId, isOpen])

	const onKeyDown = useCallback(
		(event: KeyboardEvent<HTMLButtonElement>): void => {
			const { key } = event

			if (key === 'Tab') {
				if (isOpen) {
					const highlightedType = SEARCH_TYPE_VALUES[activeIndex]
					if (highlightedType && highlightedType !== searchType) {
						onSearchTypeWillChange?.()
						clearAiSearch()
					}
					if (highlightedType) {
						setSearchType(highlightedType)
					}
					setIsOpen(false)
				}
				return
			}

			if (key === 'Escape') {
				if (isOpen) {
					event.preventDefault()
					close()
				}
				return
			}

			if (PREVENT_DEFAULT_KEYS.has(key)) {
				event.preventDefault()
			}
			if (!isOpen) {
				const opensFromClosed =
					key === 'ArrowDown' ||
					key === 'ArrowUp' ||
					key === 'Enter' ||
					key === ' ' ||
					key === 'Home' ||
					key === 'End'
				if (opensFromClosed) {
					setIsOpen(true)
					return
				}
				return
			}

			if (key === 'ArrowDown') {
				setActiveIndex((i) => keepWithinRange(i + 1))
				return
			}
			if (key === 'ArrowUp') {
				setActiveIndex((i) => keepWithinRange(i - 1))
				return
			}
			if (key === 'Home') {
				setActiveIndex(FIRST_OPTION_INDEX)
				return
			}
			if (key === 'End') {
				setActiveIndex(LAST_OPTION_INDEX)
				return
			}
			if (key === 'Enter' || key === ' ') {
				const highlightedType = SEARCH_TYPE_VALUES[activeIndex]
				if (highlightedType) {
					selectType(highlightedType)
				}
			}
		},
		[
			activeIndex,
			clearAiSearch,
			close,
			isOpen,
			onSearchTypeWillChange,
			searchType,
			selectType,
			setActiveIndex,
			setIsOpen,
			setSearchType,
		]
	)

	return {
		ids,
		rootRef,
		buttonRef,
		selected,
		activeIndex,
		isOpen,
		selectType,
		setActiveIndex,
		toggleOpen,
		onKeyDown,
	}
}
